import { useEffect, useMemo, useState } from 'react';
import {
  createInbound,
  fetchInboundDays,
  listInbound,
  listIntegrationTypes,
  listOutbound,
  pauseInbound,
  resumeInbound,
  revokeInbound,
  type DailyUsage,
  type InboundCreated,
  type InboundEntry,
  type InstallSnippet,
  type IntegrationType,
  type IntegrationTypeMeta,
  type OutboundEntry,
} from '../lib/api';

export default function InboundTokens() {
  const [types, setTypes] = useState<IntegrationTypeMeta[]>([]);
  const [entries, setEntries] = useState<InboundEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [justCreated, setJustCreated] = useState<InboundCreated | null>(null);

  useEffect(() => {
    listIntegrationTypes()
      .then((response) => setTypes(response.types))
      .catch(() => setTypes([]));
    reload();
  }, []);

  async function reload() {
    try {
      const response = await listInbound();
      setEntries(response.inbound);
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not load tokens.');
    }
  }

  return (
    <section className="block-brutal ">
      <header className="px-6 py-5 border-b-2 border-border flex items-baseline justify-between">
        <div>
          <h2 className="font-display text-xl uppercase">Inbound tokens</h2>
          <p className="text-sm text-muted-foreground mt-1">
            One typed token per client, project, or integration. Spend through each
            token will attribute back to its label.
          </p>
        </div>
        <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-spray-magenta font-bold">
          // per-client
        </div>
      </header>

      {justCreated && (
        <NewTokenBanner
          created={justCreated}
          onDismiss={() => {
            setJustCreated(null);
            reload();
          }}
        />
      )}

      <CreateForm
        types={types}
        onCreated={(created) => setJustCreated(created)}
        onError={setError}
      />

      {error && (
        <div className="px-6 py-4 border-t border-border bg-muted text-sm text-foreground font-mono">
          {error}
        </div>
      )}

      <InboundTable entries={entries} onRevoked={reload} />
    </section>
  );
}

function CreateForm({
  types,
  onCreated,
  onError,
}: {
  types: IntegrationTypeMeta[];
  onCreated: (created: InboundCreated) => void;
  onError: (message: string | null) => void;
}) {
  const [label, setLabel] = useState('');
  const [type, setType] = useState<IntegrationType | ''>('');
  const [outboundSlot, setOutboundSlot] = useState<number | null>(null);
  const [outbound, setOutbound] = useState<OutboundEntry[]>([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!type && types.length > 0) setType(types[0].id);
  }, [types, type]);

  useEffect(() => {
    listOutbound()
      .then((response) => setOutbound(response.outbound))
      .catch(() => setOutbound([]));
  }, []);

  const slotsForType = useMemo(() => {
    if (!type) return [];
    return outbound.find((entry) => entry.id === type)?.slots ?? [];
  }, [type, outbound]);

  useEffect(() => {
    if (slotsForType.length > 0) {
      setOutboundSlot(slotsForType[0].slot);
    } else {
      setOutboundSlot(null);
    }
  }, [slotsForType]);

  const noOutbound = type && slotsForType.length === 0;

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!label.trim() || !type || outboundSlot === null) return;
    setSubmitting(true);
    onError(null);
    try {
      const created = await createInbound(label.trim(), type, outboundSlot);
      onCreated(created);
      setLabel('');
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : 'Could not mint token.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="px-6 py-5 border-b-2 border-border flex flex-col gap-3"
    >
      <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-end">
        <label className="flex-1 flex flex-col gap-1.5">
          <span className="text-xs font-mono tracking-wide uppercase text-muted-foreground">
            Label
          </span>
          <input
            type="text"
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            placeholder="e.g. Acme Studio, my laptop, production agent"
            className="border-2 border-border bg-card  px-4 py-2.5 text-sm focus:outline-none focus:border-foreground"
          />
        </label>
        <label className="md:w-44 flex flex-col gap-1.5">
          <span className="text-xs font-mono tracking-wide uppercase text-muted-foreground">
            Integration
          </span>
          <select
            value={type}
            onChange={(event) => setType(event.target.value as IntegrationType)}
            className="border-2 border-border bg-card  px-4 py-2.5 text-sm focus:outline-none focus:border-foreground font-mono"
          >
            {types.map((meta) => (
              <option key={meta.id} value={meta.id}>
                {meta.label}
              </option>
            ))}
          </select>
        </label>
        <label className="md:w-56 flex flex-col gap-1.5">
          <span className="text-xs font-mono tracking-wide uppercase text-muted-foreground">
            Outbound credential
          </span>
          <select
            value={outboundSlot ?? ''}
            onChange={(event) => setOutboundSlot(Number(event.target.value))}
            disabled={slotsForType.length === 0}
            className="border-2 border-border bg-card  px-4 py-2.5 text-sm focus:outline-none focus:border-foreground font-mono disabled:opacity-60"
          >
            {slotsForType.map((slot) => (
              <option key={slot.slot} value={slot.slot}>
                {slot.label}
                {slot.pricingMode === 'subscription'
                  ? ` · $${(slot.monthlyFeeCents / 100).toFixed(0)}/mo`
                  : ' · per-token'}
              </option>
            ))}
            {slotsForType.length === 0 && <option value="">No credentials yet</option>}
          </select>
        </label>
        <button
          type="submit"
          disabled={submitting || !label.trim() || !type || outboundSlot === null}
          className="bg-spray-yellow text-primary-foreground font-medium px-5 py-3  hover:bg-primary transition disabled:opacity-60"
        >
          {submitting ? 'Minting…' : 'Generate token'}
        </button>
      </div>
      {noOutbound && (
        <div className="text-xs font-mono text-spray-yellow">
          Add a credential under Outbound credentials before minting an inbound
          token for this integration.
        </div>
      )}
    </form>
  );
}

function NewTokenBanner({
  created,
  onDismiss,
}: {
  created: InboundCreated;
  onDismiss: () => void;
}) {
  const [tokenCopied, setTokenCopied] = useState(false);

  async function copyToken() {
    await navigator.clipboard.writeText(created.token);
    setTokenCopied(true);
    setTimeout(() => setTokenCopied(false), 2000);
  }

  return (
    <div className="bg-spray-yellow text-primary-foreground border-b-2 border-foreground px-6 py-6">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-spray-magenta font-bold">
            // New token · shown once
          </div>
          <div className="font-display text-2xl uppercase mt-2 leading-tight">
            {created.label}
            <span className="text-primary-foreground/55 font-normal">
              {' '}· {created.typeLabel}
            </span>
          </div>

          <div className="mt-4 flex items-center gap-3">
            <code className="font-mono text-[13px] bg-background text-foreground border-2 border-foreground px-3 py-2.5 break-all flex-1">
              {created.token}
            </code>
            <button
              onClick={copyToken}
              className="text-xs font-bold uppercase tracking-wider bg-background text-foreground border-2 border-foreground px-4 py-2.5 hover:translate-y-[-1px] transition-transform flex-shrink-0"
            >
              {tokenCopied ? 'Copied ✓' : 'Copy'}
            </button>
          </div>

          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            <SnippetBlock snippet={created.install.quickStart} />
            <SnippetBlock snippet={created.install.profileInstall} />
          </div>

          <div className="mt-4 text-[11px] uppercase tracking-wider font-bold text-primary-foreground/70">
            Store this somewhere safe — we won&rsquo;t show it again.
          </div>
        </div>
        <button
          onClick={onDismiss}
          className="text-[10px] uppercase tracking-wider font-bold text-primary-foreground/70 hover:text-primary-foreground transition flex-shrink-0"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}

function SnippetBlock({ snippet }: { snippet: InstallSnippet }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    await navigator.clipboard.writeText(snippet.snippet);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="bg-background text-foreground border-2 border-foreground overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b-2 border-foreground bg-card">
        <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-spray-yellow font-bold">
          // {snippet.title}
        </span>
        <button
          onClick={copy}
          className="font-mono text-[10px] tracking-wider uppercase text-muted-foreground hover:text-spray-yellow transition font-bold"
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className="font-mono text-[11.5px] text-foreground p-3 overflow-x-auto whitespace-pre">
        {snippet.snippet}
      </pre>
    </div>
  );
}

function InboundTable({
  entries,
  onRevoked,
}: {
  entries: InboundEntry[] | null;
  onRevoked: () => void;
}) {
  if (entries === null) {
    return <div className="px-6 py-6 text-sm text-muted-foreground font-mono">Loading…</div>;
  }
  if (entries.length === 0) {
    return (
      <div className="px-6 py-10 text-center">
        <div className="text-muted-foreground text-sm">
          No inbound tokens yet. Mint your first one above.
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
            <th className="px-6 py-3 font-normal">Label</th>
            <th className="px-6 py-3 font-normal">Integration</th>
            <th className="px-6 py-3 font-normal">Token</th>
            <th className="px-6 py-3 font-normal text-right">Requests (this month)</th>
            <th className="px-6 py-3 font-normal text-right">Tokens in / out</th>
            <th className="px-6 py-3 font-normal text-right">Cost (est.)</th>
            <th className="px-6 py-3 font-normal text-right">Status</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => (
            <InboundRow key={entry.id} entry={entry} onChanged={onRevoked} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function InboundRow({
  entry,
  onChanged,
}: {
  entry: InboundEntry;
  onChanged: () => void;
}) {
  const revoked = entry.revokedAt !== null;
  const paused = !revoked && entry.pausedAt !== null;
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [days, setDays] = useState<DailyUsage[] | null>(null);
  const [daysError, setDaysError] = useState<string | null>(null);

  async function toggleExpand() {
    const next = !expanded;
    setExpanded(next);
    if (next && !days) {
      try {
        const response = await fetchInboundDays(entry.id);
        setDays(response.days);
      } catch (caught) {
        setDaysError(caught instanceof Error ? caught.message : 'Could not load history.');
      }
    }
  }

  async function handleRevoke() {
    if (!confirm(`Revoke "${entry.label}"? Requests using this token will fail immediately.`)) return;
    setBusy(true);
    try {
      await revokeInbound(entry.id);
    } finally {
      setBusy(false);
      onChanged();
    }
  }

  async function handleToggle() {
    setBusy(true);
    try {
      if (paused) {
        await resumeInbound(entry.id);
      } else {
        await pauseInbound(entry.id);
      }
    } finally {
      setBusy(false);
      onChanged();
    }
  }

  const usage = entry.usage;
  const requestsDisplay = usage ? usage.requests.toLocaleString() : '—';
  const tokensDisplay = usage
    ? `${formatTokens(usage.inputTokens)} / ${formatTokens(usage.outputTokens)}`
    : '—';
  const costDisplay = formatUsageCost(usage);

  const rowOpacity = revoked ? 'opacity-50' : paused ? 'opacity-75' : '';

  return (
    <>
    <tr className={'border-t border-border ' + rowOpacity}>
      <td className="px-6 py-4 text-foreground">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={toggleExpand}
            className="text-muted-foreground hover:text-foreground transition w-4 h-4 flex items-center justify-center font-mono text-[10px]"
            aria-label={expanded ? 'Collapse' : 'Expand 30-day history'}
            title={expanded ? 'Hide history' : 'Show 30-day history'}
          >
            {expanded ? '▾' : '▸'}
          </button>
          <span>{entry.label}</span>
          {paused && (
            <span className="font-mono text-[10px] tracking-wider uppercase text-spray-yellow bg-muted px-1.5 py-0.5 rounded">
              paused
            </span>
          )}
        </div>
      </td>
      <td className="px-6 py-4 font-mono text-xs text-muted-foreground">{entry.typeLabel}</td>
      <td className="px-6 py-4 font-mono text-xs text-muted-foreground">{entry.tokenPreview}</td>
      <td className="px-6 py-4 font-mono text-xs text-muted-foreground text-right tabular-nums">
        {requestsDisplay}
      </td>
      <td className="px-6 py-4 font-mono text-xs text-muted-foreground text-right tabular-nums">
        {tokensDisplay}
      </td>
      <td className="px-6 py-4 font-mono text-xs text-foreground text-right tabular-nums">
        {costDisplay}
      </td>
      <td className="px-6 py-4 text-right">
        {revoked ? (
          <span className="font-mono text-xs text-muted-foreground">
            Revoked {entry.revokedAt ? new Date(entry.revokedAt).toLocaleDateString() : ''}
          </span>
        ) : (
          <div className="flex items-center justify-end gap-3">
            <a
              href={`/_api/inbound/${encodeURIComponent(entry.id)}/export.csv`}
              className="text-xs font-medium text-muted-foreground hover:text-foreground transition"
              title="Download CSV of all months"
            >
              CSV
            </a>
            <button
              onClick={handleToggle}
              disabled={busy}
              className="text-xs font-medium text-muted-foreground hover:text-foreground transition disabled:opacity-60"
            >
              {paused ? 'Resume' : 'Pause'}
            </button>
            <button
              onClick={handleRevoke}
              disabled={busy}
              className="text-xs font-medium text-spray-yellow hover:text-foreground transition disabled:opacity-60"
            >
              Revoke
            </button>
          </div>
        )}
      </td>
    </tr>
    {expanded && (
      <tr className="border-t border-border bg-card">
        <td colSpan={7} className="px-6 py-5">
          {daysError ? (
            <div className="text-xs text-spray-yellow font-mono">{daysError}</div>
          ) : days === null ? (
            <div className="text-xs text-muted-foreground font-mono">Loading 30-day history…</div>
          ) : (
            <DailySparkline days={days} />
          )}
        </td>
      </tr>
    )}
    </>
  );
}

function DailySparkline({ days }: { days: DailyUsage[] }) {
  const filled = useMemo(() => fillMissingDays(days), [days]);
  const maxRequests = Math.max(1, ...filled.map((d) => d.requests));
  const totalRequests = filled.reduce((sum, d) => sum + d.requests, 0);
  const totalCostMicros = filled.reduce((sum, d) => sum + d.costMicros, 0);
  const totalIn = filled.reduce((sum, d) => sum + d.inputTokens, 0);
  const totalOut = filled.reduce((sum, d) => sum + d.outputTokens, 0);

  return (
    <div>
      <div className="flex items-baseline justify-between mb-3">
        <div className="font-mono text-[10.5px] tracking-wider uppercase text-spray-yellow">
          Last 30 days
        </div>
        <div className="font-mono text-[11px] text-muted-foreground">
          {totalRequests.toLocaleString()} requests · {formatTokens(totalIn)} in / {formatTokens(totalOut)} out
          {totalCostMicros > 0 && (
            <span className="text-foreground"> · {formatCostMicros(totalCostMicros)}</span>
          )}
        </div>
      </div>
      <div className="flex items-end gap-[3px] h-16">
        {filled.map((day) => {
          const heightPct = (day.requests / maxRequests) * 100;
          const isToday = day.day === filled[filled.length - 1]?.day;
          const tooltip = `${day.day}: ${day.requests.toLocaleString()} req${
            day.costMicros > 0 ? ` · ${formatCostMicros(day.costMicros)}` : ''
          }`;
          return (
            <div
              key={day.day}
              title={tooltip}
              className="flex-1 flex flex-col justify-end min-w-[6px] cursor-help"
            >
              <div
                className={
                  'w-full  transition ' +
                  (day.requests === 0
                    ? 'bg-border/40 h-[2px]'
                    : isToday
                      ? 'bg-spray-yellow'
                      : 'bg-spray-yellow/70 hover:bg-spray-yellow')
                }
                style={day.requests > 0 ? { height: `${Math.max(heightPct, 6)}%` } : undefined}
              />
            </div>
          );
        })}
      </div>
      <div className="flex justify-between mt-2 font-mono text-[10px] text-muted-foreground">
        <span>{filled[0]?.day}</span>
        <span>{filled[filled.length - 1]?.day}</span>
      </div>
    </div>
  );
}

function fillMissingDays(days: DailyUsage[]): DailyUsage[] {
  const today = new Date();
  const start = new Date(today);
  start.setUTCDate(start.getUTCDate() - 29);

  const byDay = new Map(days.map((d) => [d.day, d]));
  const out: DailyUsage[] = [];
  for (let offset = 0; offset < 30; offset++) {
    const date = new Date(start);
    date.setUTCDate(start.getUTCDate() + offset);
    const key = `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
    out.push(
      byDay.get(key) ?? {
        day: key,
        requests: 0,
        inputTokens: 0,
        outputTokens: 0,
        errors: 0,
        costMicros: 0,
      },
    );
  }
  return out;
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function formatTokens(count: number): string {
  if (count === 0) return '0';
  if (count < 1000) return count.toLocaleString();
  if (count < 1_000_000) return `${(count / 1000).toFixed(1)}k`;
  return `${(count / 1_000_000).toFixed(2)}M`;
}

function formatCostMicros(micros: number): string {
  const usd = micros / 1_000_000;
  if (usd >= 100) return `$${usd.toFixed(0)}`;
  if (usd >= 10) return `$${usd.toFixed(2)}`;
  if (usd >= 0.01) return `$${usd.toFixed(2)}`;
  return `$${usd.toFixed(4)}`;
}

function formatUsageCost(usage: NonNullable<InboundEntry['usage']> | null): React.ReactNode {
  if (!usage) return '—';
  if (usage.costMode === 'subscription') {
    if (!usage.subscriptionShare || usage.subscriptionShare.monthlyFeeCents === 0) {
      return '—';
    }
    const dollars = formatCostMicros(usage.costMicros);
    const percent = (usage.subscriptionShare.share * 100).toFixed(1);
    return (
      <span title={`${percent}% of $${(usage.subscriptionShare.monthlyFeeCents / 100).toFixed(2)}/mo subscription`}>
        {dollars}
        <span className="text-muted-foreground ml-1 text-[10px]">({percent}%)</span>
      </span>
    );
  }
  return usage.costMicros > 0 ? formatCostMicros(usage.costMicros) : '—';
}

